import { KeyValue } from '@angular/common';
import { Component, OnInit, Input } from '@angular/core';
import ShiftInfo, { getShiftPriorityOrder, TotalShiftPositions } from 'src/app/ShiftInfo';
import { SiteChart } from 'src/app/site-chart';

@Component({
  selector: 'app-site-item',
  templateUrl: './site-item.component.html',
  styleUrls: ['./site-item.component.scss']
})

export class SiteItemComponent implements OnInit {

  @Input() site: SiteChart;
  @Input() shifts: any[] = []; // Consider this as position assignments
  @Input() sitePositions: any[] = []; // Consider this as shift positions
  @Input() futureInspectors: number[] = [];

  // This will hold shiftName as a key and ShiftInfo object type as a value
  shiftsInfo: Record<string, ShiftInfo> = {};

  // Total positions will calculate total of selected fields in each shift
  totalPositions: TotalShiftPositions = {
    unassignedPositions: 0,
    totalAgencies: 0,
    totalInspectors: 0
  };

  constructor() { }

  ngOnInit(): void {
    // Clear it in case there's a new update
    this.shiftsInfo = {};

    // key: shift name, value: position IDs
    const shiftPositionIds: Record<string, number[]> = {};

    // A set prevents duplicate entries
    const shiftNames = new Set<string>();

    this.sitePositions.forEach(position => {
      const shiftName = position.shift;

      // Store all shift names
      shiftNames.add(shiftName);

      // If key exists then push the data
      if (shiftPositionIds[shiftName]) {
        shiftPositionIds[shiftName].push(position.id);
      } else {
        // If it doesn't exist, then repeat the same process above but instead initialize the array
        shiftPositionIds[shiftName] = [position.id];
      }
    });

    // Goal of this object to count agency assignments based on unique position IDs
    // This will hold shift names as a key and position IDs as a value
    const agencyAssignmentIds: Record<string, number[]> = {};

    // Goal of this object is to track duplicated inspector IDs for each position
    // This will hold position ID as a key and inspector IDs as a value
    const positionInspectorIds: Record<number, number[]> = {};

    if (this.hasNoShifts()) {
      this.shifts = [];
    }

    this.shifts.forEach(shiftItem => {
      const shiftName = shiftItem.shift;

      const isInspectorIdDuplicated = this.hasDuplicateInspectorIds(positionInspectorIds, shiftItem);

      // Make sure it doesn't increment values if it holds a duplicate inspector ID case
      if (!isInspectorIdDuplicated) {
        this.shiftsInfo[shiftName] = this.getShiftInfo(shiftName, shiftItem);
      }

      // If it's value of that shift key is not an array then make it into an array (in another word, if it doesn't have a value)
      if (!Array.isArray(agencyAssignmentIds[shiftName])) {
        agencyAssignmentIds[shiftName] = [];
      }

      // If shift type is 1 (an agency) and this position ID doesn't exist in the agencyAssignmentIds then add it to that array
      if (shiftItem.type === 1 && !agencyAssignmentIds[shiftName].includes(shiftItem.positionId)) {
        agencyAssignmentIds[shiftName].push(shiftItem.positionId);
      }
    });

    // keep in mind that it's doing shifts calculation for one site unlike the ones in branch and country pages
    shiftNames.forEach(shiftName => {
      // If there's no information for the current shift name, then assign empty values to it
      if (!this.shiftsInfo[shiftName]) {
        this.shiftsInfo[shiftName] = this.createEmptyShiftInfo();
      }

      this.shiftsInfo[shiftName].unassignedPositions = this.getUnassignedPositionsCount(shiftPositionIds, shiftName);

      // Not all shifts will have agency assignments
      if (agencyAssignmentIds[shiftName]) {
        // get length of unqiue position IDs and assign them to the totalAgencies
        this.shiftsInfo[shiftName].totalAgencies = agencyAssignmentIds[shiftName].length;
      }

      // Calculate the sum of total positions
      this.totalPositions.totalInspectors += this.shiftsInfo[shiftName].totalInspectors;
      this.totalPositions.totalAgencies += this.shiftsInfo[shiftName].totalAgencies;
      this.totalPositions.unassignedPositions += this.shiftsInfo[shiftName].unassignedPositions;
    });
  }

  getShiftInfo(shiftName: string, shift: any): ShiftInfo {
    // If this.shiftsInfo[shiftName] has a value then pick values from there. Otherwise pick the empty values
    const shiftInfo = this.shiftsInfo[shiftName]
      ? this.shiftsInfo[shiftName]
      : this.createEmptyShiftInfo();

    // An agency has type 1, an inspector has type 0
    const { totalSites, totalInspectors, totalOnSite, totalConfirmed } = shiftInfo;

    // Fill shiftsInfo key with the shiftName and incrementValue based on below conditions
    // futureInspectors can be undefined if some future inspectors don't exist in the current date
    return {
      unassignedPositions: 0,
      totalSites: shift.type === 0 ? totalSites + 1 : totalSites,
      totalAgencies: 0, // total agencies will not be incremented here, so its value will remain 0
      totalInspectors: shift.type === 0 ? totalInspectors + 1 : totalInspectors,
      totalOnSite: totalOnSite + shift.onSite,
      totalConfirmed: this.futureInspectors && this.futureInspectors.includes(shift.inspectorId) ? totalConfirmed + 1 : totalConfirmed
    };
  }

  getUnassignedPositionsCount(shiftPositionIds: Record<string, number[]>, shiftName: string): number {
    // We gotta loop through each shift position IDs with their specific shift name in order to check for IDs without assignments
    return shiftPositionIds[shiftName].reduce((previousValue, positionId) => {
      // This will check for each position ID if it has an assignment or not
      const hasPositionAssignment = this.shifts.some(shiftItem => {
        // We must loop through each shift and check if shift names match and if their position IDs match
        return shiftItem.shift === shiftName && shiftItem.positionId === positionId;
      });

      // If that ID doesn't have an assignment then it's an unassigned or empty position
      if (!hasPositionAssignment) {
        return previousValue + 1;
      }

      return previousValue;
    }, 0);
  }

  // Angular will return 2 pairs of type KeyValue which will have the type I specified below
  // First type param of KeyValue will present the key (shift name) and the second one will present the value (ShiftInfo)
  sortShifts(a: KeyValue<string, ShiftInfo>, b: KeyValue<string, ShiftInfo>): number {
    const priorityA = getShiftPriorityOrder(a.key);
    const priorityB = getShiftPriorityOrder(b.key);

    if (priorityA > priorityB) {
      return 1;
    }

    if (priorityA < priorityB) {
      return -1;
    }

    return 0;
  }

  hasNoShifts(): boolean {
    // If it's not an array or if it's an array but it's empty
    return !Array.isArray(this.shifts) || (Array.isArray(this.shifts) && !this.shifts.length);
  }

  createEmptyShiftInfo(): ShiftInfo {
    return {
      unassignedPositions: 0,
      totalSites: 0,
      totalAgencies: 0,
      totalInspectors: 0,
      totalOnSite: 0,
      totalConfirmed: 0
    };
  }

  // Returns true if a duplicate inspector ID is found
  hasDuplicateInspectorIds(positionInspectorIds: Record<number, number[]>, shiftAssignment: any): boolean {
    // Check if current assignment doesn't belong to an inspector
    if (shiftAssignment.type !== 0) {
      return false;
    }

    // If positionInspectorIds doesn't hold a key of the position ID then assign the inspector ID to it
    if (!positionInspectorIds[shiftAssignment.positionId]) {
      positionInspectorIds[shiftAssignment.positionId] = [shiftAssignment.inspectorId];
      return false;
    }

    // Check if the position holds the same inspector ID, if so then it's a duplicate ID
    if (positionInspectorIds[shiftAssignment.positionId].includes(shiftAssignment.inspectorId)) {
      return true;
    }

    // otherwise push it to this position ID
    positionInspectorIds[shiftAssignment.positionId].push(shiftAssignment.inspectorId);

    return false;
  }

}
